import { useCallback, useState } from 'react';
import { useShowContext } from 'framework/show';
import { useMessageService, ErrorMessage, SuccessMessage } from 'framework/ui/Message';
import { useDashboardCallbacks } from './useDashboardCallbacks';

interface IssuesFormData {
  id: string;
  issues: Array<{
    id?: string | null;
    description: string;
    status: string;
    targetPriority: string;
  }>;
}

export const useStaffingDashboardModals = (refetch?: () => Promise<void>) => {
  const { data } = useShowContext();
  const { addMessage } = useMessageService();
  const { handleIssuesSubmit: handleIssuesSubmitCallback, handleFollowUpSubmit: handleFollowUpsSubmitCallback } =
    useDashboardCallbacks(refetch);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleIssuesSubmit = useCallback(
    async (formData: IssuesFormData) => {
      setIsSubmitting(true);
      try {
        await handleIssuesSubmitCallback(formData);
        addMessage(new SuccessMessage('Issues updated successfully'));
      } catch (error) {
        addMessage(new ErrorMessage('Failed to update issues. Please try again.'));
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [handleIssuesSubmitCallback, addMessage],
  );

  const handleFollowUpSubmit = useCallback(
    async (formData: any) => {
      setIsSubmitting(true);
      try {
        await handleFollowUpsSubmitCallback(formData);
        addMessage(new SuccessMessage('Follow-up updated successfully'));
      } catch (error) {
        addMessage(new ErrorMessage('Failed to update follow-up. Please try again.'));
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [handleFollowUpsSubmitCallback, addMessage],
  );

  const projectData = data?.getData();

  return {
    handleIssuesSubmit,
    handleFollowUpSubmit,
    isSubmitting,
    projectData,
  };
};
